import { Children, isValidElement, ReactElement, ReactNode } from 'react';
import { GridColumnProps } from '@progress/kendo-react-grid';

export interface IColumnInfo {
  field: string;
  width: number;
  isFixed: boolean;
}

export const SIDEBAR_WIDTH = 60;
export const GRID_BUFFER = 25;
export const MIN_COL_WIDTH = 50;

export const calculateFixedColumnsWidth = (children: ReactNode): number => {
  let fixedWidth = 0;
  Children.forEach(children, (child: ReactElement<GridColumnProps>) => {
    if (!isValidElement(child)) return;
    const isFixed = child.props.resizable === false;
    const width = Number(child.props.width);
    if (isFixed && width) {
      fixedWidth += width;
    }
  });
  return fixedWidth;
};

export const parseStoredWidth = (
  savedValue: string | number | undefined,
  defaultWidth: number | undefined,
  flexibleSpace: number
): number | undefined => {
  if (!savedValue) return defaultWidth;
  const storedString = String(savedValue);
  if (!storedString.includes('%')) return defaultWidth;
  const percentage = parseFloat(storedString.replace('%', ''));
  return !isNaN(percentage) && flexibleSpace > 0
    ? (percentage / 100) * flexibleSpace
    : defaultWidth;
};

export const distributeRemainingSpace = (
  columns: IColumnInfo[],
  columnsWithoutWidth: string[],
  currentGridWidth: number
): IColumnInfo[] => {
  if (columnsWithoutWidth.length === 0) return columns;

  const usedWidth = columns.reduce((sum, column) => sum + column.width, 0);
  const widthPerColumn = Math.max(
    MIN_COL_WIDTH,
    (currentGridWidth - usedWidth - GRID_BUFFER) / columnsWithoutWidth.length
  );

  const newColumns = columnsWithoutWidth.map((field) => ({
    field,
    width: widthPerColumn,
    isFixed: false
  }));

  return [...columns, ...newColumns];
};

export const adjustFlexibleColumns = (
  columns: IColumnInfo[],
  currentGridWidth: number,
  hasColumnsWithoutWidth: boolean
): IColumnInfo[] => {
  const totalWidth = columns.reduce((sum, column) => sum + column.width, 0);
  const overflow = currentGridWidth - totalWidth - GRID_BUFFER;

  if (overflow === 0) return columns;

  const flexibleColumns = columns.filter((column) => !column.isFixed);
  const flexibleTotal = flexibleColumns.reduce(
    (sum, column) => sum + column.width,
    0
  );

  if (flexibleTotal > 0 && (overflow < 0 || !hasColumnsWithoutWidth)) {
    return columns.map((column) => {
      if (column.isFixed) return column;
      return {
        ...column,
        width: column.width + overflow * (column.width / flexibleTotal)
      };
    });
  }

  return columns;
};
